/**
 * Writes a recording blob into a per-day subfolder inside the user-chosen
 * directory, i.e.  <chosen folder>/<YYYY-MM-DD>/<filename>.
 *
 * Returns the relative path it was saved as (e.g. "2026-06-18/john-doe.webm").
 * Caller is responsible for having already secured read/write permission via
 * ensureReadWritePermission().
 */
interface WritableFileStream {
  write: (data: Blob | BufferSource | string) => Promise<void>;
  close: () => Promise<void>;
}

interface WritableCapableFileHandle {
  createWritable: (options?: {
    keepExistingData?: boolean;
  }) => Promise<WritableFileStream>;
}

export async function saveAudioToFolder(
  handle: FileSystemDirectoryHandle,
  blob: Blob,
  fileName?: string,
): Promise<string> {
  /* 1. Get (or create) today's date folder, e.g. "2026-06-18" */
  const dateFolder = dateFolderName();
  const dirHandle = await handle.getDirectoryHandle(dateFolder, {
    create: true,
  });

  /* 2. Resolve a safe, unique filename inside that folder */
  const ext = extensionFor(blob);
  const base = sanitizeBaseName(fileName) || defaultBaseName();
  const name = await uniqueFileName(dirHandle, base, ext);

  /* 3. Write the blob */
  const fileHandle = await dirHandle.getFileHandle(name, { create: true });
  const writable = await (
    fileHandle as unknown as WritableCapableFileHandle
  ).createWritable();
  await writable.write(blob);
  await writable.close();

  return `${dateFolder}/${name}`;
}

/* ── Helpers ─────────────────────────────────────────────── */

function dateFolderName(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Strip the extension and any characters illegal in a filename. */
function sanitizeBaseName(raw?: string): string {
  if (!raw) return "";
  return raw
    .trim()
    .replace(/\.[^/.]+$/, "") // drop any extension the user typed
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_") // illegal chars → underscore
    .replace(/\s+/g, " ")
    .slice(0, 120);
}

function defaultBaseName(): string {
  const stamp = new Date()
    .toISOString()
    .replace(/[:.]/g, "-")
    .replace("T", "_")
    .slice(0, 19);
  return `recording-${stamp}`;
}

function extensionFor(blob: Blob): string {
  return blob.type.includes("mp4")
    ? "m4a"
    : blob.type.includes("ogg")
      ? "ogg"
      : blob.type.includes("wav")
        ? "wav"
        : "webm";
}

/**
 * Ensures we never silently overwrite an existing recording: if "name.ext"
 * exists, try "name (1).ext", "name (2).ext", …
 */
async function uniqueFileName(
  dir: FileSystemDirectoryHandle,
  base: string,
  ext: string,
): Promise<string> {
  let candidate = `${base}.${ext}`;
  let counter = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      await dir.getFileHandle(candidate, { create: false });
      // It exists — bump the counter and retry.
      candidate = `${base} (${counter}).${ext}`;
      counter += 1;
    } catch {
      // NotFoundError → this name is free.
      return candidate;
    }
  }
}
